import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from './client'
import { v2Unscoped, v2api } from './v2'
import { useSessionStore } from '@/stores/sessionStore'
import { useSysConfig } from './config'
import type { Store, Register } from '@/types'

export const storeKeys = {
  all: ['stores'] as const,
  list: () => [...storeKeys.all, 'list'] as const,
  detail: (id: number) => [...storeKeys.all, 'detail', id] as const,
  registers: (storeId: number) => [...storeKeys.all, 'registers', storeId] as const,
}

interface StoresListResponse {
  stores: Store[]
}

export function useStoresList() {
  return useQuery({
    queryKey: storeKeys.list(),
    queryFn: async (): Promise<Store[]> => {
      const data = await api.get<StoresListResponse>('/ht/api/select-store')
      return data.stores
    },
    staleTime: 1000 * 60 * 30,
  })
}

// Login responses only return store_id, so selectedStore.name starts as a
// "Store 49" placeholder. Once we have a session, fetch the real store and
// merge the missing fields back in.
export function useBackfillSelectedStore() {
  const selectedStore = useSessionStore((s) => s.selectedStore)
  const setStore = useSessionStore((s) => s.setStore)
  const { data } = useStore(selectedStore?.id ?? null)

  useEffect(() => {
    if (!selectedStore || !data) return
    const realName = typeof data.name === 'string' ? data.name.trim() : ''
    const placeholder = `Store ${selectedStore.id}`

    // BE returns `cannabis_limit` as a Decimal — could be number or
    // numeric string. Coerce to a plain number for the session store;
    // downstream readers (e.g. the new-customer limit default) can
    // `.toFixed(2)` it back to a wire string when needed.
    const cannabisLimitRaw = data.cannabis_limit
    const cannabisLimit = (() => {
      if (cannabisLimitRaw == null) return null
      const n = typeof cannabisLimitRaw === 'number'
        ? cannabisLimitRaw
        : parseFloat(String(cannabisLimitRaw))
      return Number.isFinite(n) ? n : null
    })()

    // Name-backfill is one-shot — only when the cached name is empty
    // or the synthesized "Store {id}" placeholder, never overwriting
    // a user-displayed name. Cannabis limit, address, etc. backfill
    // whenever they're missing on the session and the BE has them.
    const needsName =
      !!realName
      && (!selectedStore.name || selectedStore.name === placeholder)
      && realName !== selectedStore.name
    const needsLimit =
      cannabisLimit != null && selectedStore.cannabis_limit == null

    if (!needsName && !needsLimit) return

    setStore({
      ...selectedStore,
      name: needsName ? realName : selectedStore.name,
      address: typeof data.address === 'string' ? data.address : selectedStore.address,
      city: typeof data.city === 'string' ? data.city : selectedStore.city,
      province: typeof data.province === 'string' ? data.province : selectedStore.province,
      postal_code: typeof data.postal_code === 'string' ? data.postal_code : selectedStore.postal_code,
      phone: typeof data.phone === 'string' ? data.phone : selectedStore.phone,
      cannabis_limit: cannabisLimit ?? selectedStore.cannabis_limit ?? null,
    })
  }, [data, selectedStore, setStore])
}

export function useStore(storeId: number | null) {
  return useQuery({
    queryKey: storeKeys.detail(storeId!),
    queryFn: async (): Promise<Record<string, unknown>> => {
      // Some legacy endpoints wrap the payload in `{ store: {...} }`.
      // Keep the response loose and let consumers extract the fields they
      // need via the helpers below.
      const data = await v2api.get<Record<string, unknown> | { store: Record<string, unknown> }>(
        v2Unscoped(`/objects/stores/${storeId}`),
      )
      if (data && typeof data === 'object' && 'store' in data && (data as { store: unknown }).store) {
        return (data as { store: Record<string, unknown> }).store
      }
      return data as Record<string, unknown>
    },
    enabled: !!storeId,
    staleTime: 1000 * 60 * 30,
  })
}

// Legacy installs scatter tax labels across a few different naming
// conventions (Hypnotoad sys_config keys, store object columns, even the
// active-customer payload). Try every well-known shape so the cashier
// always sees the real label (e.g. "GST", "HST", "PST") instead of "Tax".
const TAX_NAME_KEYS: Array<[string, string, string, string]> = [
  // sys_config patterns we've seen in the wild
  ['tax.tax1.name', 'tax.tax2.name', 'tax.tax3.name', 'tax.tax4.name'],
  ['system.tax.tax1.name', 'system.tax.tax2.name', 'system.tax.tax3.name', 'system.tax.tax4.name'],
  ['system.tax1.name', 'system.tax2.name', 'system.tax3.name', 'system.tax4.name'],
  ['pos.tax.tax1.name', 'pos.tax.tax2.name', 'pos.tax.tax3.name', 'pos.tax.tax4.name'],
]

const TAX_INCLUSIVE_KEYS = [
  'pos.price_before_taxes',
  'system.tax.included',
  'tax.included',
]

function pickTaxNamesFromConfig(config: Record<string, unknown> | undefined): string[] {
  if (!config) return []
  for (const set of TAX_NAME_KEYS) {
    const names = set
      .map((key) => {
        const value = config[key]
        return typeof value === 'string' ? value.trim() : ''
      })
      .filter((n): n is string => n.length > 0)
    if (names.length > 0) return names
  }
  return []
}

// Mirror the receipt's taxes_dict: only slots flagged as selling taxes
// (taxN_s) with a name are shown.
function pickSellingTaxNames(group: Record<string, unknown> | null | undefined): string[] {
  if (!group) return []
  return [1, 2, 3, 4]
    .filter((i) => group[`tax${i}_s`] === true)
    .map((i) => group[`tax${i}_name`])
    .map((n) => (typeof n === 'string' ? n.trim() : ''))
    .filter((n) => n.length > 0)
}

// A store with no Selling Taxes group is taxed with the default group
// (SA_Taxes.get_taxes: is_default, not deleted, first by name DESC).
// objects/taxes already drops deleted groups and sorts by name ASC, so the
// last default in the list is the one the BE picks.
function useDefaultTaxGroup(enabled: boolean) {
  return useQuery({
    queryKey: ['taxes', 'default'],
    queryFn: async (): Promise<Record<string, unknown> | null> => {
      const groups = await api.get<Array<Record<string, unknown>>>('/ht/api/objects/taxes')
      const defaults = groups.filter((g) => g.is_default === true)
      return defaults[defaults.length - 1] ?? null
    },
    enabled,
    staleTime: 1000 * 60 * 30,
  })
}

function pickTaxNamesFromStore(store: Record<string, unknown> | null | undefined): string[] {
  if (!store) return []
  // The legacy store object exposes tax labels under tax1_name…tax4_name,
  // but newer API revisions occasionally use `tax_1_name` or nest them
  // under `taxes: [{ name }]`. Cover all of those.
  const direct = ['tax1_name', 'tax2_name', 'tax3_name', 'tax4_name']
    .map((k) => (typeof store[k] === 'string' ? (store[k] as string).trim() : ''))
    .filter((n): n is string => n.length > 0)
  if (direct.length > 0) return direct

  const underscored = ['tax_1_name', 'tax_2_name', 'tax_3_name', 'tax_4_name']
    .map((k) => (typeof store[k] === 'string' ? (store[k] as string).trim() : ''))
    .filter((n): n is string => n.length > 0)
  if (underscored.length > 0) return underscored

  const taxesArr = store.taxes
  // /objects/stores/:id nests the store's Selling Taxes group as
  // `taxes: { tax1_name, tax1_s, ... }` (null when none is set).
  if (taxesArr && typeof taxesArr === 'object' && !Array.isArray(taxesArr)) {
    return pickSellingTaxNames(taxesArr as Record<string, unknown>)
  }
  if (Array.isArray(taxesArr)) {
    const fromArr = taxesArr
      .map((t) => {
        if (!t || typeof t !== 'object') return ''
        const name = (t as Record<string, unknown>).name
        return typeof name === 'string' ? name.trim() : ''
      })
      .filter((n): n is string => n.length > 0)
    if (fromArr.length > 0) return fromArr
  }

  return []
}

/**
 * Returns a human-readable label for the active tax(es) configured on the
 * current store, e.g. "HST", "GST, PST", "Tax". The legacy POS exposes up
 * to four tax slots — sometimes via /ht/api/objects/stores/:id, sometimes
 * via sys_config — so we check every known location and join the populated
 * names with ", ". When prices are configured to include tax we suffix
 * "incl." so the cashier knows the line is informational only.
 */
export function useStoreTaxLabel(): {
  label: string
  taxNames: string[]
  pricesIncludeTax: boolean
  isLoading: boolean
} {
  const { selectedStore } = useSessionStore()
  const { data: storeDetail, isLoading: isLoadingStore } = useStore(selectedStore?.id ?? null)
  const sysConfigKeys = useMemo(() => {
    const keys = new Set<string>(TAX_INCLUSIVE_KEYS)
    for (const set of TAX_NAME_KEYS) for (const k of set) keys.add(k)
    return Array.from(keys)
  }, [])
  const { data: sysConfig, isLoading: isLoadingConfig } = useSysConfig(sysConfigKeys)
  const { data: defaultGroup, isLoading: isLoadingDefault } = useDefaultTaxGroup(
    !!storeDetail && storeDetail.taxes == null,
  )

  return useMemo(() => {
    const fromConfig = pickTaxNamesFromConfig(sysConfig)
    const fromStore = pickTaxNamesFromStore(storeDetail)
    const fromSession = pickTaxNamesFromStore(
      selectedStore as unknown as Record<string, unknown> | null,
    )
    const taxNames = fromConfig.length > 0
      ? fromConfig
      : fromStore.length > 0
        ? fromStore
        : fromSession.length > 0
          ? fromSession
          : pickSellingTaxNames(defaultGroup)

    let pricesIncludeTax = false
    if (sysConfig) {
      for (const key of TAX_INCLUSIVE_KEYS) {
        const value = sysConfig[key]
        // `pos.price_before_taxes === true` => prices DO NOT include tax.
        // The other keys are true when prices already include tax.
        if (key === 'pos.price_before_taxes') {
          if (value === false) { pricesIncludeTax = true; break }
        } else if (value === true) {
          pricesIncludeTax = true
          break
        }
      }
    }

    const joined = taxNames.length > 0 ? taxNames.join(', ') : 'Tax'
    const label = pricesIncludeTax && taxNames.length > 0
      ? `${joined} incl.`
      : joined

    return {
      label,
      taxNames,
      pricesIncludeTax,
      isLoading: isLoadingStore || isLoadingConfig || isLoadingDefault,
    }
  }, [storeDetail, selectedStore, sysConfig, defaultGroup, isLoadingStore, isLoadingConfig, isLoadingDefault])
}

interface RawRegister {
  id: number
  name: string
  store?: number
  store_id?: number
  terminal_id?: number | string
  is_safe?: boolean
  local_printing_enabled?: boolean
}

export function useRegisters(storeId: number | null, storeName?: string) {
  const numericStoreId = storeId ? Number(storeId) : null

  return useQuery({
    queryKey: storeKeys.registers(numericStoreId!),
    queryFn: async (): Promise<Register[]> => {
      const data = await v2api.get<RawRegister[]>(v2Unscoped('/objects/pos_registers'))

      const filtered = (Array.isArray(data) ? data : [])
        .filter((r: RawRegister) => {
          const registerStoreId = Number(r.store ?? r.store_id)
          return registerStoreId === numericStoreId && !r.is_safe
        })

      return filtered.map((r: RawRegister) => ({
        id: r.id,
        name: r.name,
        terminal_id: String(r.terminal_id || r.id),
        store_id: Number(r.store ?? r.store_id),
        store_name: storeName || `Store ${numericStoreId}`,
        is_active: true,
        last_activity: null,
        local_printing_enabled: !!r.local_printing_enabled,
      }))
    },
    enabled: !!numericStoreId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  })
}
